import { setFarmName } from '../../core/actions';
import { exportString, importString, SaveError } from '../../core/save';
import { audioSettings, setSound, soundOn } from '../../platform/audio';
import { clearGameState, newGame } from '../../platform/storage';
import { sendFeedback, setTelemetry } from '../../platform/telemetry';
import { game } from '../controller';
import { h } from '../dom';
import { closeModal, confirmModal, openModal } from '../modals';
import { section, type Panel } from '../shop';
import { toast } from '../toasts';

export const THEMES: [string, string][] = [
  ['twilight', 'Twilight Barn'], ['midnight', 'Midnight Coop'], ['nightroost', 'Night Roost'], ['ember', 'Ember Roost'],
  ['forest', 'Forest Floor'], ['copper', 'Copper Sunset'], ['plum', 'Plum Harvest'], ['stormy', 'Stormy Coop'],
  ['meadow', 'Morning Meadow'], ['sunny', 'Sunny Coop'], ['lavender', 'Lavender Fields'], ['sandy', 'Sandy Shores'],
  ['cherry', 'Cherry Blossom'], ['hc-light', 'High contrast — light (AAA)'], ['hc-dark', 'High contrast — dark (AAA)'],
];

export function applyTheme(theme: string): void {
  document.documentElement.dataset.theme = theme;
}

function row(label: string, control: Node): HTMLElement {
  return h('div', { class: 'kv', style: 'align-items:center' }, h('span', {}, label), control);
}

function slider(value: number, onInput: (v: number) => void): HTMLInputElement {
  const el = h('input', { type: 'range', min: 0, max: 1, step: 0.05, value });
  el.addEventListener('input', () => onInput(Number(el.value)));
  return el;
}

export const settingsPanel: Panel = {
  id: 'settings',
  label: 'Settings',
  key: () => `${soundOn()}|${game.prefs.theme}|${audioSettings.isReady()}`,
  build(root: HTMLElement) {
    const farm = section('🏡 Farm');
    const name = h('input', { type: 'text', value: game.state.farmName, maxlength: 30, 'aria-label': 'Farm name' });
    farm.append(row('Farm name', h('div', { class: 'row' }, name, h('button', { class: 'btn small', type: 'button', onclick: () => { setFarmName(game.state, name.value); game.changed(); toast('Farm renamed.', 'good'); } }, 'Save'))));
    const theme = h('select', { 'aria-label': 'Theme' }, ...THEMES.map(([v, l]) => h('option', { value: v, selected: v === game.prefs.theme }, l)));
    theme.addEventListener('change', () => { game.setPrefs({ theme: theme.value }); applyTheme(theme.value); });
    farm.append(row('Theme', theme));
    const coach = h('input', { type: 'checkbox', checked: game.prefs.coach });
    coach.addEventListener('change', () => game.setPrefs({ coach: coach.checked }));
    farm.append(row('Show next-goal hints', coach));
    root.append(farm);

    const audio = section('🔊 Sound & music', 'sound effects play only when you click');
    const on = h('input', { type: 'checkbox', checked: soundOn() });
    on.addEventListener('change', () => { setSound(on.checked); game.setPrefs({ sound: on.checked }); });
    audio.append(row('Sound on', on));
    if (audioSettings.isReady()) {
      const sfx = h('select', {}, ...audioSettings.themes().map((t) => h('option', { value: t, selected: t === audioSettings.theme() }, t)));
      sfx.addEventListener('change', () => audioSettings.setTheme(sfx.value));
      audio.append(row('Sound style', sfx));
      const mode = h('select', {}, h('option', { value: 'random', selected: audioSettings.musicMode() === 'random' }, 'Shuffle every 5 min'), h('option', { value: 'specific', selected: audioSettings.musicMode() === 'specific' }, 'Pick a track'));
      mode.addEventListener('change', () => audioSettings.setMusicMode(mode.value as 'random' | 'specific'));
      audio.append(row('Music', mode));
      const track = h('select', {}, ...audioSettings.musicKeys().map((k) => h('option', { value: k, selected: k === audioSettings.music() }, audioSettings.musicLabel(k))));
      track.addEventListener('change', () => { audioSettings.setMusicMode('specific'); audioSettings.setMusic(track.value); });
      audio.append(row('Track', h('div', { class: 'row' }, track, h('button', { class: 'btn small secondary', type: 'button', onclick: () => audioSettings.shuffle() }, 'Shuffle'))));
      audio.append(row('Master volume', slider(audioSettings.master(), audioSettings.setMaster)));
      audio.append(row('Effects volume', slider(audioSettings.sfx(), audioSettings.setSfx)));
      audio.append(row('Music volume', slider(audioSettings.music_(), audioSettings.setMusicVolume)));
    } else {
      audio.append(h('p', { class: 'hint', text: 'Turn sound on to pick a sound style and music.' }));
    }
    root.append(audio);

    const save = section('💾 Save');
    save.append(h('p', { class: 'hint', text: 'Your farm saves automatically in this browser. Export a backup string to move it or keep it safe.' }));
    save.append(h('div', { class: 'row' },
      h('button', { class: 'btn small', type: 'button', onclick: showExport }, 'Export save'),
      h('button', { class: 'btn small secondary', type: 'button', onclick: showImport }, 'Import save'),
      h('button', { class: 'btn small danger', type: 'button', onclick: resetFarm }, 'Start over')));
    root.append(save);

    const privacy = section('🔒 Privacy');
    const tele = h('input', { type: 'checkbox', checked: game.prefs.telemetry });
    tele.addEventListener('change', () => { game.setPrefs({ telemetry: tele.checked }); setTelemetry(tele.checked); });
    privacy.append(row('Share anonymous gameplay stats', tele));
    privacy.append(h('p', { class: 'hint', text: 'Stats use a random ID (never your farm name) and help tune the game. No personal data is collected.' }));
    root.append(privacy);

    const fb = section('💬 Feedback');
    const cat = h('select', {}, ...['Bug', 'Idea', 'Confusing', 'Balance', 'Love it'].map((c) => h('option', { value: c }, c)));
    const text = h('textarea', { rows: 3, placeholder: 'What happened, or what would make it better?', style: 'width:100%' });
    fb.append(cat, h('div', { style: 'height:6px' }), text, h('div', { class: 'row', style: 'margin-top:6px' },
      h('button', { class: 'btn small', type: 'button', onclick: async () => {
        if (!text.value.trim()) return toast('Write something first.', 'warn');
        const ok = await sendFeedback(cat.value, text.value, { money: game.state.money, hens: game.state.flocks.length, won: game.state.won, ua: navigator.userAgent });
        toast(ok ? 'Thanks! Feedback sent.' : "Couldn't reach the server — try again later.", ok ? 'good' : 'warn');
        if (ok) text.value = '';
      } }, 'Send')));
    root.append(fb);
    root.append(h('p', { class: 'hint', text: 'Hen House v1.0.0 · a relaxing chicken-farm idle game.' }));
  },
};

function showExport(): void {
  const str = exportString(game.state);
  const area = h('textarea', { rows: 6, style: 'width:100%', readonly: true }, str);
  openModal(h('div', {}, h('h2', { text: 'Export save' }), h('p', { text: 'Copy this string somewhere safe.' }), area,
    h('div', { class: 'actions' },
      h('button', { class: 'btn secondary', type: 'button', onclick: () => closeModal() }, 'Close'),
      h('button', { class: 'btn', type: 'button', onclick: async () => {
        try { await navigator.clipboard.writeText(str); toast('Copied!', 'good'); } catch { area.select(); document.execCommand('copy'); }
      } }, 'Copy'))));
  area.select();
}

function showImport(): void {
  const area = h('textarea', { rows: 6, style: 'width:100%', placeholder: 'Paste your save string' });
  openModal(h('div', {}, h('h2', { text: 'Import save' }), h('p', { text: 'This replaces your current farm.' }), area,
    h('div', { class: 'actions' },
      h('button', { class: 'btn secondary', type: 'button', onclick: () => closeModal() }, 'Cancel'),
      h('button', { class: 'btn danger', type: 'button', onclick: () => {
        try {
          game.replaceState(importString(area.value));
          closeModal();
          toast('Save imported.', 'good');
        } catch (e) {
          toast(e instanceof SaveError ? e.message : 'Import failed.', 'warn');
        }
      } }, 'Import'))));
}

function resetFarm(): void {
  confirmModal('Start over?', 'Your whole farm will be deleted. Export a save first if you might want it back.', 'Continue', () => {
    confirmModal('Really start over?', 'This cannot be undone.', 'Delete my farm', () => {
      clearGameState();
      game.replaceState(newGame());
      game.setPrefs({ introDone: false });
      window.location.reload();
    }, true);
  }, true);
}
